/**
 * Endless Carousel — Infinite marquee Web Component.
 *
 * Usage:
 *   <endless-carousel data-max-width="1200">
 *     <div data-carousel-track>
 *       <div>Item 1</div>
 *       <div>Item 2</div>
 *     </div>
 *   </endless-carousel>
 *
 * Lazy: cloning + animation starts only when the element enters
 * the viewport (IntersectionObserver with rootMargin for early start).
 * Clones once for data-max-width (default 1200px) — no ResizeObserver needed.
 * Hover pauses the animation (CSS-side via :hover).
 */
class EndlessCarousel extends HTMLElement {
  #track = null;
  #mutationObserver = null;
  #intersectionObserver = null;
  #originalItems = [];
  #initialized = false;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');

    if (!this.#track || this.#track.children.length === 0) {
      this.#mutationObserver = new MutationObserver(() => {
        this.#track = this.querySelector('[data-carousel-track]');
        if (this.#track && this.#track.children.length > 0) {
          this.#mutationObserver.disconnect();
          this.#mutationObserver = null;
          this.#observe();
        }
      });
      this.#mutationObserver.observe(this, { childList: true, subtree: true });
      return;
    }

    this.#observe();
  }

  disconnectedCallback() {
    this.#mutationObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver = null;
    this.#intersectionObserver = null;
  }

  /* Wait until element is near viewport before doing any work */
  #observe() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.#intersectionObserver.disconnect();
          this.#intersectionObserver = null;
          this.#init();
        }
      },
      { rootMargin: '200px' }
    );
    this.#intersectionObserver.observe(this);
  }

  #init() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#originalItems = Array.from(this.#track.children);
    this.#fill();
  }

  async #fill() {
    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const maxWidth = parseInt(this.getAttribute('data-max-width')) || 1200;

    /* Measure original set width */
    let originalWidth = 0;
    this.#originalItems.forEach((item) => {
      originalWidth += item.offsetWidth + gap;
    });

    if (originalWidth === 0) return;

    /* Clone enough to fill max viewport + one original set */
    const needed = Math.ceil((maxWidth + originalWidth) / originalWidth);
    for (let i = 0; i < needed; i++) {
      const fragment = document.createDocumentFragment();
      this.#originalItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.removeAttribute('id');
        fragment.appendChild(clone);
      });
      this.#track.appendChild(fragment);
      await this.#yieldToMain();
    }

    /* Set scroll distance to exactly one original set */
    this.#track.style.setProperty('--ec-scroll-distance', `-${originalWidth}px`);
  }

  #yieldToMain() {
    if ('scheduler' in window && 'yield' in scheduler) {
      return scheduler.yield();
    }
    return new Promise((resolve) => setTimeout(resolve, 0));
  }
}

customElements.define('endless-carousel', EndlessCarousel);
